import { useHandler } from "@redotech/react-util/hook";
import { PreviousOrderCountSegment } from "@redotech/redo-conversion-core-common/post-purchase/post-purchase-flow";
import {
  PostPurchaseSegmentNumberOperator,
  postPurchaseSegmentNumberOperatorToFriendlyString,
} from "@redotech/redo-conversion-core-common/post-purchase/segment-types";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { OperatorSelector } from "../../operator-selector";

interface PreviousOrderCountSegmentCommonProps {
  condition: PreviousOrderCountSegment;
}

export type PreviousOrderCountSegmentProps =
  PreviousOrderCountSegmentCommonProps &
    (
      | {
          readonly: false;
          setCondition: (condition: PreviousOrderCountSegment) => void;
        }
      | { readonly: true }
    );

export const PreviousOrderCountSegmentCondition = memo(
  function PreviousOrderCountSegmentCondition({
    condition,
    ...props
  }: PreviousOrderCountSegmentProps) {
    const handleOperatorSelected = useHandler(
      (operator: PostPurchaseSegmentNumberOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, operator });
        }
      },
    );

    const handleNumOrdersChange = useHandler((numOrders: number) => {
      if (!props.readonly) {
        props.setCondition({ ...condition, numOrders });
      }
    });

    return props.readonly ? (
      <Text fontSize="xs">
        Customer's previous order count is{" "}
        {postPurchaseSegmentNumberOperatorToFriendlyString[condition.operator]}{" "}
        {condition.numOrders}
      </Text>
    ) : (
      <>
        <Text fontSize="xs" textColor="primary">
          Customer's previous order count is
        </Text>
        <OperatorSelector<PostPurchaseSegmentNumberOperator>
          availableOptions={Object.values(PostPurchaseSegmentNumberOperator)}
          onOperatorSelected={handleOperatorSelected}
          operatorToText={postPurchaseSegmentNumberOperatorToFriendlyString}
          readonly={props.readonly}
          selectedOperator={condition.operator}
        />
        <RedoIncrementDecrement
          min={0}
          setValue={handleNumOrdersChange}
          size="small"
          step={1}
          value={condition.numOrders}
        />
      </>
    );
  },
);
